


import {
  defineComponent,
  computed,
  onMounted,
  watch,
  onUnmounted,
  PropType,
} from '@vue/composition-api';

import RealtimeModePane from '@/components/ExternalReports/EmergencyResponseTeam/components/RealtimeView/RealtimeModePane/index.vue';
import PhotoModal from '@/components/lib/PhotoModal/index.vue';
import MapMiscInfo from '@/components/ExternalReports/EmergencyResponseTeam/components/common/MapMiscInfo.vue';
import type {
  EmergencyResponseTeamMtxExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamMtx';
import type {
  EmergencyResponseTeamReportPhotoExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamReport';
import type {
  EmergencyResponseTeamCarExt,
} from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamCar';
import ManualDownloadLinks from '@/components/ExternalReports/EmergencyResponseTeam/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/EmergencyResponseTeam/composables/usePaneControl';
import { useExtremeMap } from '@/components/ExternalReports/EmergencyResponseTeam/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/EmergencyResponseTeam/composables/usePhotoModal';
import {
  CAR_LIST_MAX_HEIGHT_RATE,
  CAR_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/EmergencyResponseTeam/consts';
import { fetchImageAsObjectURL, requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';
import { useRequestGuard } from '@/composables/useRequestGuard';
import useEmergencyResponseTeamCars from '@/components/ExternalReports/EmergencyResponseTeam/composables/useCars';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  setup(props) {
    const {
      paneState,
      onChangeShowCarIcons: onChangeShowCarIconsOrig,
    } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: CAR_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: CAR_LIST_MAX_HEIGHT_RATE,
      },
    });

    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      redrawCarLayer,
      showEmergencyResponseTeamCarPopup,
      showEmergencyResponseTeamReportPhotoPopup,
      showEmergencyResponseTeamMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);

    const { doRequest } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const {
      carState,
      startCarUpdateInterval,
      stopCarUpdateInterval,
      selectCar,
      deselectCar,
      deselectSelectedCarReportPhotos,
      redrawCarLayerWatchValue,
    } = useEmergencyResponseTeamCars();

    watch(
      redrawCarLayerWatchValue,
      (value, oldValue) => {
        const fitToExtent = oldValue === 0 || oldValue === undefined;
        redrawEmergencyResponseTeamCarLayer({ fitToExtent });
      },
    );

    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
    } = usePhotoModal();
    const showPhotoModal = async(photoType: string, photos: EmergencyResponseTeamReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      showPhotoModalOrig(photoType, photos);
    };

    const movingCarsCount = computed<number>(() => {
      return carState.cars.filter(car => car.isMoving).length;
    });
    const stoppedCarsCount = computed<number>(() => {
      return carState.cars.filter(car => !car.isMoving).length;
    });

    interface RedrawEmergencyResponseTeamCarLayerOptions {
      fitToExtent?: boolean;
      setSelectedCarToCenter?: boolean;
    }
    const redrawEmergencyResponseTeamCarLayer = (options: RedrawEmergencyResponseTeamCarLayerOptions = {}) => {
      redrawCarLayer(options);
    };

    const onChangeShowCarIcons = () => {
      onChangeShowCarIconsOrig();
      redrawEmergencyResponseTeamCarLayer();
    };

    const onClickCar = async(selectedCar: EmergencyResponseTeamCarExt) => {
      hidePopup();
      // 選択中の車両をクリックした場合、選択を解除する
      if (deselectCar(selectedCar.id)) {
        redrawEmergencyResponseTeamCarLayer();
        return;
      }
      await doRequest(async() => {
        await selectCar(selectedCar.id);
      }, '車両の取得に失敗しました。');
      const updated = carState.cars.find(c => c.id === selectedCar.id);
      showEmergencyResponseTeamCarPopup(updated ?? selectedCar);
      redrawEmergencyResponseTeamCarLayer({ setSelectedCarToCenter: true });
    };

    const onClickPhotoPin = async(photo: EmergencyResponseTeamReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }
        showEmergencyResponseTeamReportPhotoPopup(photo);
      }
      redrawEmergencyResponseTeamCarLayer();
    };
    const onClickMtx = (mtx: EmergencyResponseTeamMtxExt) => {
      const isSelected = mtx.isSelected;
      hidePopup();
      deselectSelectedCarReportPhotos();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showEmergencyResponseTeamMtxPopup(mtx);
      }
      redrawEmergencyResponseTeamCarLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedCarReportPhotos();
      redrawEmergencyResponseTeamCarLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    onMounted(async() => {
      await doRequest(async() => {
        await loadExtremeMapEssentials();
        await startCarUpdateInterval(props.userSettings);
      }, '車両の取得に失敗しました。');
    });

    onUnmounted(() => {
      stopCarUpdateInterval();
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      mapViewState,
      paneState,
      carState,
      photoModalState,
      // ref
      extremeMapRef,
      // methods
      onClickCar,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      onChangeShowCarIcons,
      paneResizeStopped,
      showPhotoModal,
      hidePhotoModal,
      movingCarsCount,
      stoppedCarsCount,
    };
  },
  components: {
    RealtimeModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
